import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { LockKeyIcon, WarningOctagonIcon, ArrowRightIcon, CheckCircleIcon } from "@phosphor-icons/react";

export default function CreditBlocks() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const canAuth = hasPermission("clients.override_credit_block");
  const [solicitudes, setSolicitudes] = useState([]);
  useEffect(() => {
    api.get("/credit-requests", { params: { estado: "pendiente" } }).then(({ data }) => setSolicitudes(data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/clients/blocked"); setRows(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const stats = rows.reduce((a, r) => {
    if (r.bloqueado) a.bloqueados++;
    if (r.saldo_vencido > 0) a.vencidos++;
    a.valor += r.saldo_vencido;
    return a;
  }, { bloqueados: 0, vencidos: 0, valor: 0 });

  return (
    <Layout
      title="Bloqueos y autorizaciones de crédito"
      subtitle={`${stats.bloqueados} clientes bloqueados · ${stats.vencidos} con saldo vencido · ${fmtL(stats.valor)} en riesgo`}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <Kpi label="Clientes bloqueados" value={stats.bloqueados} color="text-red-400" border="border-red-400/30" icon={LockKeyIcon} />
        <Kpi label="Con saldo vencido" value={stats.vencidos} color="text-agro-yellow" border="border-agro-yellow/30" icon={WarningOctagonIcon} />
        <Kpi label="Valor total vencido" value={fmtL(stats.valor)} color="text-text-primary" border="border-border" />
      </div>

      {solicitudes.length > 0 && (
        <div className="bg-card border border-agro-yellow/40 rounded-lg p-4 mb-5" data-testid="credit-requests-pending">
          <div className="font-heading text-agro-yellow mb-2">Solicitudes de desbloqueo pendientes ({solicitudes.length})</div>
          <div className="divide-y divide-border/40">
            {solicitudes.map((s) => (
              <button key={s.id} onClick={() => navigate(`/clientes/${s.cliente_id}?tab=bitacora`)}
                className="w-full text-left py-2 text-sm flex items-center gap-3 hover:text-agro-green">
                <span className="font-mono text-xs">{s.numero}</span>
                <span className="font-medium">{s.cliente_nombre}</span>
                <span className="text-text-muted text-xs truncate flex-1">{s.motivo}</span>
                {s.promesa && <span className="text-xs">Promesa {fmtL(s.promesa.monto)} · {fmtDate(s.promesa.fecha)}</span>}
                <span className="text-xs text-text-muted">{s.solicitante}</span>
                <ArrowRightIcon size={14} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Cliente</th>
              <th className="p-3">RTC</th>
              <th className="p-3 text-right">Límite</th>
              <th className="p-3 text-right">Saldo</th>
              <th className="p-3 text-right">% usado</th>
              <th className="p-3 text-right">Saldo vencido</th>
              <th className="p-3 text-right">Días máx</th>
              <th className="p-3">Estado</th>
              <th className="p-3 pr-5 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="9" className="p-12 text-center text-text-muted"><CheckCircleIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> No hay clientes bloqueados ni en riesgo</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`blocked-row-${r.codigo}`}>
                <td className="p-3 pl-5">
                  <div className="font-mono text-xs text-agro-green">{r.codigo}</div>
                  <div className="text-sm">{r.nombre}</div>
                </td>
                <td className="p-3 text-xs text-text-secondary">{r.rtc_asignado_nombre || "—"}</td>
                <td className="p-3 text-right text-xs">{fmtL(r.limite_credito)}</td>
                <td className="p-3 text-right text-xs">{fmtL(r.saldo_total)}</td>
                <td className="p-3 text-right" data-testid={`blocked-pct-${r.codigo}`}>
                  <div className={`text-sm font-medium ${r.porcentaje_uso_credito >= 100 ? "text-red-400" : r.porcentaje_uso_credito >= 80 ? "text-agro-yellow" : "text-text-secondary"}`}>
                    {r.limite_credito > 0 ? `${r.porcentaje_uso_credito}%` : "sin límite"}
                  </div>
                  {r.limite_credito > 0 && (
                    <div className="h-1 w-20 ml-auto mt-1 bg-app rounded overflow-hidden">
                      <div className={`h-full ${r.porcentaje_uso_credito >= 100 ? "bg-red-400" : r.porcentaje_uso_credito >= 80 ? "bg-agro-yellow" : "bg-agro-green"}`}
                        style={{ width: `${Math.min(100, r.porcentaje_uso_credito)}%` }} />
                    </div>
                  )}
                </td>
                <td className="p-3 text-right text-sm font-medium text-red-400">{fmtL(r.saldo_vencido)}</td>
                <td className="p-3 text-right text-xs">{r.max_dias_vencido > 0 ? `${r.max_dias_vencido}d` : "—"}</td>
                <td className="p-3">
                  {r.bloqueado ? (
                    <span className="px-2 py-0.5 rounded text-[10px] border border-red-400/40 text-red-400 bg-red-500/10 inline-flex items-center gap-1">
                      <LockKeyIcon size={10} weight="fill" /> Bloqueado
                    </span>
                  ) : r.autorizado_vigente ? (
                    <span className="px-2 py-0.5 rounded text-[10px] border border-agro-green/40 text-agro-green bg-agro-green/10">
                      Autorizado hasta {fmtDate(r.autorizado_hasta)}
                    </span>
                  ) : r.porcentaje_uso_credito >= 80 ? (
                    <span className="px-2 py-0.5 rounded text-[10px] border border-agro-yellow/40 text-agro-yellow bg-agro-yellow/10">
                      Cerca del límite
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] border border-border text-text-secondary">Normal</span>
                  )}
                </td>
                <td className="p-3 pr-5 text-right">
                  <button onClick={() => navigate(`/clientes/${r.id}`)}
                    data-testid={`view-blocked-${r.codigo}`}
                    className={`px-2.5 py-1 text-xs border rounded flex items-center gap-1 ml-auto ${
                      canAuth && r.bloqueado ? "border-agro-yellow/40 text-agro-yellow hover:bg-agro-yellow/10" : "border-border text-text-secondary hover:text-text-primary"
                    }`}>
                    {canAuth && r.bloqueado ? "Autorizar" : "Ver detalle"} <ArrowRightIcon size={11} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

function Kpi({ label, value, color, border, icon: Icon }) {
  return (
    <div className={`bg-card border ${border} rounded-lg p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={12} className={color} />} {label}
      </div>
      <div className={`text-2xl font-heading font-semibold ${color}`}>{value}</div>
    </div>
  );
}
